import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Clock, 
  PlayCircle, 
  CheckCircle, 
  AlertCircle,
  GripVertical,
  Calendar,
  Target,
  Flag,
  Sparkles,
  RefreshCw,
  TrendingUp,
  BookOpen,
  Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const KanbanPDIIA = ({ colaboradorId }) => {
  const [loading, setLoading] = useState(false);
  const [kanbanData, setKanbanData] = useState(null);
  const [acoes, setAcoes] = useState([]);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dropZoneActive, setDropZoneActive] = useState(null);

  useEffect(() => {
    if (colaboradorId) {
      carregarKanban();
    }
  }, [colaboradorId]);

  const carregarKanban = async () => {
    try {
      setLoading(true);
      
      // Tentar buscar kanban existente
      try {
        const response = await axios.get(
          `${API}/api/pdi-completo/kanban-ia/${colaboradorId}`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }
        );
        setKanbanData(response.data);
        setAcoes(response.data.acoes_kanban || []);
      } catch (error) {
        if (error.response?.status === 404) {
          // Não há kanban ainda, gerar novo
          await gerarKanban();
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Erro ao carregar kanban:', error);
      if (error.response?.status === 404) {
        toast.error('Execute o Diagnóstico IA primeiro para gerar o Kanban.');
      } else {
        toast.error('Erro ao carregar Kanban');
      }
    } finally {
      setLoading(false);
    }
  };

  const gerarKanban = async () => {
    try {
      setLoading(true);
      toast.info('Gerando Kanban personalizado com IA...', { duration: 3000 });
      
      const response = await axios.post(
        `${API}/api/pdi-completo/kanban-ia?colaborador_id=${colaboradorId}&limite_acoes=15`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setKanbanData(response.data);
      setAcoes(response.data.acoes_kanban || []);
      
      toast.success(`Kanban criado! ${response.data.total_acoes} ações planejadas.`);
    } catch (error) {
      console.error('Erro ao gerar kanban:', error);
      if (error.response?.status === 404) {
        toast.error('Execute o Diagnóstico IA primeiro para gerar o Kanban.');
      } else {
        toast.error('Erro ao gerar Kanban');
      }
    } finally {
      setLoading(false);
    }
  };

  const colunas = [
    { id: 'to_do', titulo: 'A Fazer', icon: Clock, color: 'bg-blue-500', bgColor: 'bg-blue-50' },
    { id: 'in_progress', titulo: 'Em Progresso', icon: PlayCircle, color: 'bg-yellow-500', bgColor: 'bg-yellow-50' },
    { id: 'done', titulo: 'Concluído', icon: CheckCircle, color: 'bg-green-500', bgColor: 'bg-green-50' }
  ];

  const agruparAcoesPorCategoria = () => {
    const grupos = {};
    colunas.forEach(col => {
      grupos[col.id] = acoes?.filter(a => a.categoria === col.id) || [];
    });
    return grupos;
  };

  const acoesAgrupadas = agruparAcoesPorCategoria();

  // Funções de Drag and Drop
  const handleDragStart = (e, acao, categoriaOrigem) => {
    setDraggedCard({ acao, categoriaOrigem });
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedCard(null);
  };

  const handleDragOver = (e, colunaId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropZoneActive(colunaId);
  };

  const handleDragLeave = (e) => {
    // Verificar se realmente saiu da zona
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDropZoneActive(null);
  };

  const handleDrop = (e, novaCategoria) => {
    e.preventDefault();
    setDropZoneActive(null);
    
    if (!draggedCard) return;

    const { acao, categoriaOrigem } = draggedCard;
    
    // Se soltou na mesma coluna, não faz nada
    if (categoriaOrigem === novaCategoria) {
      setDraggedCard(null);
      return;
    }

    // Atualizar estado local
    const acoesAtualizadas = acoes.map(a => {
      if (a.titulo === acao.titulo && a.categoria === categoriaOrigem) {
        return { ...a, categoria: novaCategoria };
      }
      return a;
    });

    setAcoes(acoesAtualizadas);
    
    // Atualizar kanbanData.distribuicao
    if (kanbanData?.distribuicao) {
      const novaDistribuicao = { ...kanbanData.distribuicao };
      novaDistribuicao[categoriaOrigem] = (novaDistribuicao[categoriaOrigem] || 0) - 1;
      novaDistribuicao[novaCategoria] = (novaDistribuicao[novaCategoria] || 0) + 1;
      
      setKanbanData({
        ...kanbanData,
        acoes_kanban: acoesAtualizadas,
        distribuicao: novaDistribuicao
      });
    }

    // Feedback visual
    const nomesCol = {
      'to_do': 'A Fazer',
      'in_progress': 'Em Progresso',
      'done': 'Concluído'
    };
    toast.success(`"${acao.titulo}" movido para "${nomesCol[novaCategoria]}"`);
    
    setDraggedCard(null);
  };

  const getPrioridadeColor = (prioridade) => {
    const colors = {
      'alta': 'bg-red-100 text-red-800 border-red-300',
      'media': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'baixa': 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[prioridade] || colors['media'];
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      'estudo': <BookOpen className="h-4 w-4" />,
      'pratica': <Target className="h-4 w-4" />,
      'projeto': <TrendingUp className="h-4 w-4" />,
      'mentoria': <Target className="h-4 w-4" />,
      'avaliacao': <CheckCircle className="h-4 w-4" />
    };
    return icons[tipo] || icons['estudo'];
  };

  const getTipoColor = (tipo) => {
    const colors = {
      'estudo': 'bg-blue-100 text-blue-800',
      'pratica': 'bg-purple-100 text-purple-800',
      'projeto': 'bg-orange-100 text-orange-800',
      'mentoria': 'bg-green-100 text-green-800',
      'avaliacao': 'bg-pink-100 text-pink-800'
    };
    return colors[tipo] || colors['estudo'];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-600 rounded-lg">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Kanban PDI</h3>
                <p className="text-sm text-gray-600 font-normal">
                  Ações de desenvolvimento geradas por IA
                </p>
              </div>
            </div>
            <Button 
              onClick={gerarKanban} 
              disabled={loading}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Gerando...' : 'Atualizar'}
            </Button>
          </CardTitle>
        </CardHeader>
        
        {kanbanData && (
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              {colunas.map(col => {
                const acoesReais = acoesAgrupadas[col.id] || [];
                return (
                  <div key={col.id} className="bg-white rounded-lg p-3">
                    <div className={`text-2xl font-bold ${col.color.replace('bg-', 'text-')}`}>
                      {acoesReais.length}
                    </div>
                    <div className="text-sm text-gray-600">{col.titulo}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Kanban Board */}
      {kanbanData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {colunas.map(coluna => {
            const acoesColuna = acoesAgrupadas[coluna.id] || [];
            const Icon = coluna.icon;

            return (
              <Card key={coluna.id} className={`border-2 ${coluna.bgColor} ${
                dropZoneActive === coluna.id ? 'ring-4 ring-purple-300 ring-opacity-50' : ''
              }`}>
                <CardHeader className={`${coluna.bgColor} border-b-2`}>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon className={`h-5 w-5 ${coluna.color.replace('bg-', 'text-')}`} />
                      <span>{coluna.titulo}</span>
                    </div>
                    <Badge variant="secondary">{acoesColuna.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent 
                  className="p-4 space-y-3 min-h-[500px]"
                  onDragOver={(e) => handleDragOver(e, coluna.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, coluna.id)}
                >
                  {acoesColuna.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      <Icon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma ação nesta coluna</p>
                    </div>
                  ) : (
                    acoesColuna.map((acao, idx) => (
                      <Card 
                        key={idx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, acao, coluna.id)}
                        onDragEnd={handleDragEnd}
                        className="border hover:border-purple-300 transition-all hover:shadow-md cursor-move"
                      >
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Header do Card */}
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-2 flex-1">
                                <GripVertical className="h-4 w-4 text-gray-400" />
                                <h4 className="font-semibold text-sm line-clamp-2">{acao.titulo}</h4>
                              </div>
                              {acao.prioridade && (
                                <Badge className={`${getPrioridadeColor(acao.prioridade)} text-xs ml-2`}>
                                  {acao.prioridade}
                                </Badge>
                              )}
                            </div>

                            {/* Descrição */}
                            <p className="text-xs text-gray-600 line-clamp-3">{acao.descricao}</p>

                            {/* Competência Relacionada */}
                            {acao.competencia_relacionada && (
                              <div className="bg-purple-50 rounded p-2 border border-purple-200">
                                <div className="flex items-center space-x-1">
                                  <Target className="h-3 w-3 text-purple-600" />
                                  <span className="text-xs font-semibold text-purple-900">
                                    {acao.competencia_relacionada}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Badges */}
                            <div className="flex flex-wrap gap-2">
                              {acao.tipo_acao && (
                                <Badge className={`${getTipoColor(acao.tipo_acao)} text-xs flex items-center gap-1`}>
                                  {getTipoIcon(acao.tipo_acao)}
                                  {acao.tipo_acao}
                                </Badge>
                              )}
                              {acao.prazo_estimado_dias && (
                                <Badge variant="outline" className="text-xs flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {acao.prazo_estimado_dias}d
                                </Badge>
                              )}
                            </div>

                            {/* Critérios de Conclusão */}
                            {acao.criterios_conclusao && acao.criterios_conclusao.length > 0 && (
                              <div className="bg-blue-50 rounded p-2 border border-blue-200">
                                <div className="text-xs font-semibold text-blue-900 mb-1">
                                  Critérios de Conclusão:
                                </div>
                                <ul className="list-disc list-inside text-xs text-gray-700 space-y-0.5">
                                  {acao.criterios_conclusao.slice(0, 2).map((criterio, i) => (
                                    <li key={i} className="line-clamp-1">{criterio}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Recursos Relacionados */}
                            {acao.recursos_relacionados && acao.recursos_relacionados.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {acao.recursos_relacionados.slice(0, 2).map((recurso, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">
                                    {recurso}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {/* Tags */}
                            {acao.tags && acao.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-200">
                                {acao.tags.slice(0, 3).map((tag, i) => (
                                  <span key={i} className="text-xs text-gray-500">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Justificativa */}
                            {acao.justificativa && (
                              <div className="text-xs text-gray-600 italic pt-2 border-t border-gray-200">
                                💡 {acao.justificativa}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Observações da IA */}
      {kanbanData?.observacoes_ia && (
        <Card className="border-purple-200">
          <CardHeader className="bg-purple-50">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <span>Observações da IA sobre Priorização</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-gray-700">{kanbanData.observacoes_ia}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !kanbanData && (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="p-12 text-center">
            <TrendingUp className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Kanban PDI com IA
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Clique em "Atualizar" para que a IA analise seu diagnóstico PDI e crie 
              um kanban personalizado com ações concretas para desenvolver suas competências
            </p>
            <Button 
              onClick={gerarKanban}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Gerar Kanban com IA
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KanbanPDIIA;
